import { useState, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, SlidersHorizontal, X, CalendarIcon } from "lucide-react";
import {
  PIPELINE_STAGES,
  PIPELINE_STAGE_LABELS,
  PIPELINE_STAGE_COLORS,
  SERVICE_TYPES,
  SERVICE_TYPE_SHORT_LABELS,
  type PipelineStage,
} from "@/types/quotes";
import {
  type QuoteFilters,
  DEFAULT_FILTERS,
  countActiveFilters,
  hasActiveFilters,
  useQuoteAttributionOptions,
} from "@/hooks/useFilteredQuotes";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface QuotesFilterBarProps {
  filters: QuoteFilters;
  onChange: (filters: QuoteFilters) => void;
  /** Hide stage filter in pipeline view since stages are columns */
  hideStageFilter?: boolean;
  totalCount?: number;
  /** Rendered first in the primary row (the page's tabs and leads chip). */
  leading?: ReactNode;
  /** Rendered after the result count (the pipeline's show-lost toggle). In the More panel on mobile. */
  trailing?: ReactNode;
  /** Rendered at the bottom of the More panel (admin-only page actions). */
  panelExtra?: ReactNode;
}

interface AttributionSelectProps {
  label: string;
  value: string | null;
  options: string[] | undefined;
  isError: boolean;
  emptyLabel: string;
  errorLabel: string;
  onChange: (value: string | null) => void;
}

/** Ad source / campaign dropdown (#811) */
function AttributionSelect({ label, value, options, isError, emptyLabel, errorLabel, onChange }: AttributionSelectProps) {
  const list = options ?? [];
  // Keep a selected value renderable even if it's not in the loaded options.
  const items = value && !list.includes(value) ? [value, ...list] : list;
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <Select value={value ?? "__all__"} onValueChange={(v) => onChange(v === "__all__" ? null : v)}>
        <SelectTrigger className="w-[180px] h-9" aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All</SelectItem>
          {items.map((v) => (
            <SelectItem key={v} value={v}>{v}</SelectItem>
          ))}
          {items.length === 0 && (isError || options) && (
            <SelectItem value="__none__" disabled>{isError ? errorLabel : emptyLabel}</SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

export function QuotesFilterBar({ filters, onChange, hideStageFilter, totalCount, leading, trailing, panelExtra }: QuotesFilterBarProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const isMobile = useIsMobile();
  const advancedCount = countActiveFilters(filters) - (filters.stages.length > 0 && !hideStageFilter ? 1 : 0);

  const { data: eventTypes } = useQuery({
    queryKey: ["event_types_filter"],
    queryFn: async () => {
      const { data } = await supabase.from("event_types").select("id, name").eq("is_active", true).order("name");
      return data ?? [];
    },
  });
  const attribution = useQuoteAttributionOptions();

  const toggleArrayFilter = <K extends keyof QuoteFilters>(key: K, value: string) => {
    const arr = filters[key] as string[];
    const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
    onChange({ ...filters, [key]: next });
  };

  const hasAnyFilter = hasActiveFilters(filters);

  const stageChips = !hideStageFilter && (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mr-0.5">Stage</span>
      {PIPELINE_STAGES.map((stage) => {
        const active = filters.stages.includes(stage);
        return (
          <button
            key={stage}
            onClick={() => toggleArrayFilter("stages", stage)}
            className={cn(
              "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
              active
                ? PIPELINE_STAGE_COLORS[stage] + " border-current"
                : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
            )}
          >
            {PIPELINE_STAGE_LABELS[stage]}
          </button>
        );
      })}
    </div>
  );

  // Result count, clear-all and the page's trailing control. On mobile they
  // live in the More panel so the primary row stays one line (#708).
  const summary = (
    <div className={cn("flex flex-wrap items-center gap-2", !isMobile && "ml-auto")}>
      {totalCount !== undefined && (
        <span className="text-xs text-muted-foreground">{totalCount.toLocaleString()} results</span>
      )}
      {hasAnyFilter && (
        <button
          onClick={() => onChange(DEFAULT_FILTERS)}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
        >
          <X className="h-3 w-3" /> Clear all
        </button>
      )}
      {trailing}
    </div>
  );

  return (
    <Collapsible open={moreOpen} onOpenChange={setMoreOpen} className="space-y-2">
      {/* Primary filter row */}
      <div className={cn("flex items-center gap-2", isMobile ? "flex-nowrap" : "flex-wrap")}>
        {leading}

        {/* Search */}
        <div className={cn("relative flex-1", isMobile ? "min-w-0" : "min-w-[200px] max-w-sm")}>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name, email, quote #..."
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            className="pl-9 h-9"
          />
        </div>

        {/* Stage chips (list view only) */}
        {!isMobile && stageChips}

        {/* More filters toggle */}
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label="More filters"
            className={cn("relative h-9 shrink-0 gap-1.5", isMobile && "w-9 px-0")}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {!isMobile && "More"}
            {advancedCount > 0 && (
              <Badge
                className={cn(
                  "h-5 w-5 p-0 flex items-center justify-center text-[10px] bg-primary text-primary-foreground",
                  isMobile && "absolute -top-2 -right-2",
                )}
              >
                {advancedCount}
              </Badge>
            )}
          </Button>
        </CollapsibleTrigger>

        {!isMobile && summary}
      </div>

      {isMobile && stageChips}

      {/* Advanced filters panel */}
      <CollapsibleContent>
        <div className="space-y-3 p-3 rounded-lg border bg-muted/20">
          {isMobile && summary}
          <div className="flex flex-wrap items-end gap-3">
            {/* Service type */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Service Type</label>
              <Select
                value={filters.serviceType ?? "__all__"}
                onValueChange={(v) => onChange({ ...filters, serviceType: v === "__all__" ? null : v })}
              >
                <SelectTrigger className="w-[160px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  {SERVICE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{SERVICE_TYPE_SHORT_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Event type */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Event Type</label>
              <Select
                value={filters.eventTypeId ?? "__all__"}
                onValueChange={(v) => onChange({ ...filters, eventTypeId: v === "__all__" ? null : v })}
              >
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  {eventTypes?.map((et) => (
                    <SelectItem key={et.id} value={et.id}>{et.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <AttributionSelect
              label="Ad Source"
              value={filters.utmSource}
              options={attribution.data?.sources}
              isError={attribution.isError}
              emptyLabel="No tracked sources yet"
              errorLabel="Couldn't load sources"
              onChange={(v) => onChange({ ...filters, utmSource: v })}
            />

            <AttributionSelect
              label="Ad Campaign"
              value={filters.utmCampaign}
              options={attribution.data?.campaigns}
              isError={attribution.isError}
              emptyLabel="No tracked campaigns yet"
              errorLabel="Couldn't load campaigns"
              onChange={(v) => onChange({ ...filters, utmCampaign: v })}
            />

            {/* Date from */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Event After</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className={cn("w-[140px] h-9 justify-start font-normal", !filters.dateFrom && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                    {filters.dateFrom ? format(new Date(filters.dateFrom + "T00:00:00"), "MMM d, yyyy") : "Any"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateFrom ? new Date(filters.dateFrom + "T00:00:00") : undefined}
                    onSelect={(d) => onChange({ ...filters, dateFrom: d ? format(d, "yyyy-MM-dd") : null })}
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date to */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Event Before</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className={cn("w-[140px] h-9 justify-start font-normal", !filters.dateTo && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                    {filters.dateTo ? format(new Date(filters.dateTo + "T00:00:00"), "MMM d, yyyy") : "Any"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateTo ? new Date(filters.dateTo + "T00:00:00") : undefined}
                    onSelect={(d) => onChange({ ...filters, dateTo: d ? format(d, "yyyy-MM-dd") : null })}
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Has value */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Value</label>
              <Select
                value={filters.hasValue === null ? "__all__" : filters.hasValue ? "yes" : "no"}
                onValueChange={(v) => onChange({ ...filters, hasValue: v === "__all__" ? null : v === "yes" })}
              >
                <SelectTrigger className="w-[120px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any</SelectItem>
                  <SelectItem value="yes">Has value</SelectItem>
                  <SelectItem value="no">$0 only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {panelExtra && <div className="border-t pt-3">{panelExtra}</div>}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
